import { useEffect, useState } from "react";
import { format, startOfMonth } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { money, todayStr } from "../lib/helpers";
import { PageHeader, Field, StatCard } from "../components/Common";
import { Input } from "../components/ui/input";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

function GroupTable({ title, rows, lang, testId }) {
  return (
    <div className="rounded-[var(--radius)] border bg-card p-6" data-testid={testId}>
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      {!rows.length && <p className="text-sm text-muted-foreground">—</p>}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between text-sm"><span className="truncate pr-4">{r.name}</span><span className="font-medium">{money(r.total, r.currency, lang)}</span></div>
        ))}
      </div>
    </div>
  );
}

export default function Reports() {
  const { locationId } = useApp();
  const { t, lang } = useT();
  const [from, setFrom] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [to, setTo] = useState(todayStr());
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/reports", { params: { date_from: from, date_to: to, location_id: locationId || undefined } }).then((x) => setR(x.data));
  }, [from, to, locationId]);
  if (!r) return <p className="text-muted-foreground">Loading…</p>;
  const currencies = Object.keys(r.revenue_totals);

  return (
    <div className="space-y-8">
      <PageHeader title={t("reports")}>
        <Field label={t("from")}><Input type="date" data-testid="reports-from" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label={t("to")}><Input type="date" data-testid="reports-to" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger">
        <StatCard testId="report-revenue" label={t("revenue")} value={currencies.length ? currencies.map((c) => <div key={c} className="text-xl">{money(r.revenue_totals[c].total, c, lang)}</div>) : "–"} />
        <StatCard testId="report-appointments" label={t("appointments")} value={Object.values(r.status_counts).reduce((a, b) => a + b, 0)} />
        <StatCard testId="report-no-shows" label={t("status_no_show")} value={<div>{r.status_counts.no_show || 0}
          <div className="text-xs font-normal text-muted-foreground mt-1">{Object.entries(r.no_show_lost).map(([c, v]) => money(v, c, lang)).join(" · ")}</div></div>} />
        <StatCard testId="report-clients" label={`${t("clients")} (new / returning)`} value={`${r.clients.new} / ${r.clients.returning}`} />
      </div>
      <div className="rounded-[var(--radius)] border bg-card p-6" data-testid="report-revenue-chart">
        <h3 className="text-lg font-semibold mb-4">{t("revenue")} / {t("day")}</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={r.by_day}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="date" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              {currencies.map((c, i) => <Bar key={c} dataKey={c} fill={COLORS[i % 4]} radius={[6, 6, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <GroupTable testId="report-by-location" title={t("locations")} rows={r.by_location} lang={lang} />
        <GroupTable testId="report-by-service" title={t("services")} rows={r.by_service} lang={lang} />
        <GroupTable testId="report-by-professional" title={t("professional")} rows={r.by_professional} lang={lang} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-[var(--radius)] border bg-card p-6"><h3 className="text-lg font-semibold mb-4">{t("status")}</h3>
          {Object.entries(r.status_counts).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1"><span>{t(`status_${k}`)}</span><span className="font-medium">{v}</span></div>)}</div>
        <div className="rounded-[var(--radius)] border bg-card p-6"><h3 className="text-lg font-semibold mb-4">Top {t("services")}</h3>
          {r.top_services.map((s) => <div key={s.name} className="flex justify-between text-sm py-1"><span>{s.name}</span><span className="font-medium">{s.count}</span></div>)}</div>
        <div className="rounded-[var(--radius)] border bg-card p-6"><h3 className="text-lg font-semibold mb-4">{t("source")}</h3>
          {r.sources.map((s) => <div key={s.name} className="flex justify-between text-sm py-1"><span className="capitalize">{s.name.replace(/_/g, " ")}</span><span className="font-medium">{s.count}</span></div>)}</div>
      </div>
    </div>
  );
}
